import { useEffect, useId, useRef, useState, type KeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { ChartColumn, Table2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './controls';
import { EmptyState, ErrorState, LoadingState } from './feedback';
import { roundShares } from '../../domain/percent';
import { CategoryDot } from './text';

/**
 * Every chart lives in a ChartContainer: title, optional controls, legend,
 * the four states (ready / loading / empty / error) and a table view, so
 * identity is never carried by color alone and every chart is readable
 * without seeing it.
 */
export type ChartState = 'ready' | 'loading' | 'empty' | 'error';

export interface ChartTable {
  columns: string[];
  rows: (string | number)[][];
}

export function ChartContainer({
  title,
  description,
  actions,
  legend,
  state = 'ready',
  onRetry,
  emptyTitle,
  emptyBody,
  table,
  children,
  hideTitle,
}: {
  title: string;
  /** The surrounding section already shows the name; keep it for assistive tech only. */
  hideTitle?: boolean;
  description?: string;
  actions?: ReactNode;
  legend?: ReactNode;
  state?: ChartState;
  onRetry?: () => void;
  emptyTitle?: string;
  emptyBody?: string;
  table?: ChartTable;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const [asTable, setAsTable] = useState(false);
  const titleId = useId();
  return (
    <figure className="chart" aria-labelledby={titleId}>
      <header className={hideTitle && !actions && !description ? 'visually-hidden' : 'chart-header'}>
        <div>
          <h3 id={titleId} className={hideTitle ? 'visually-hidden' : 'chart-title'}>
            {title}
          </h3>
          {description && <figcaption className="chart-description">{description}</figcaption>}
        </div>
        {actions && <div className="chart-actions">{actions}</div>}
      </header>
      <div className="chart-body">
        {state === 'loading' && <LoadingState variant="chart" />}
        {state === 'error' && <ErrorState compact onRetry={onRetry} />}
        {state === 'empty' && <EmptyState compact title={emptyTitle ?? t('states.chartEmptyTitle')} body={emptyBody} />}
        {state === 'ready' && (asTable && table ? <DataTable table={table} /> : children)}
      </div>
      {state === 'ready' && legend && !asTable && <div className="chart-legend">{legend}</div>}
      {state === 'ready' && table && (
        <div className="chart-footer">
          <Button variant="ghost" icon={asTable ? ChartColumn : Table2} onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
            {asTable ? t('chart.showChart') : t('chart.showTable')}
          </Button>
        </div>
      )}
    </figure>
  );
}

function DataTable({ table }: { table: ChartTable }) {
  return (
    <div className="data-table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {table.columns.map((c, i) => (
              <th key={i} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((r, i) => (
            <tr key={i}>
              {r.map((cell, j) =>
                j === 0 ? (
                  <th key={j} scope="row">
                    {cell}
                  </th>
                ) : (
                  <td key={j}>{cell}</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface Segment {
  key: string;
  label: string;
  value: number;
  color: string;
}

/**
 * One horizontal bar split by category. Segments are drawn in the order
 * given — callers pass category (series-slot) order, never size order, so
 * the validated color adjacency holds. 2px surface gaps separate fills.
 */
export function StackedBar({ segments, label, format }: { segments: Segment[]; label: string; format: (share: number) => string }) {
  const [hover, setHover] = useState<string | null>(null);
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0);
  if (total <= 0) return <div className="stacked stacked-empty" aria-label={label} role="img" />;
  const centers = new Map<string, number>();
  let offset = 0;
  for (const s of segments) {
    const share = Math.max(0, s.value) / total;
    centers.set(s.key, offset + share / 2);
    offset += share;
  }
  const hovered = segments.find((s) => s.key === hover);
  const center = hovered ? centers.get(hovered.key)! : 0;
  return (
    <div className="stacked-wrap">
      <div className="stacked" role="img" aria-label={label} onMouseLeave={() => setHover(null)}>
        {segments.map((s) => (
          <span
            key={s.key}
            className="stacked-seg"
            style={{ flexGrow: Math.max(0, s.value) / total, background: s.color }}
            onMouseEnter={() => setHover(s.key)}
          />
        ))}
      </div>
      {hovered && (
        <span
          className="chart-tip"
          role="presentation"
          style={{ left: `clamp(0px, calc(${center * 100}% - 60px), calc(100% - 140px))` }}
        >
          <CategoryDot color={hovered.color} size={8} />
          {hovered.label} · {format(Math.max(0, hovered.value) / total)}
        </span>
      )}
    </div>
  );
}

/** Legend rows under a chart: dot, name, value in text ink (never the series color). */
export function Legend({ items }: { items: { key: string; label: string; color: string; value?: ReactNode }[] }) {
  return (
    <ul className="legend">
      {items.map((i) => (
        <li key={i.key} className="legend-item">
          <CategoryDot color={i.color} />
          <span className="legend-label">{i.label}</span>
          {i.value !== undefined && <span className="legend-value num">{i.value}</span>}
        </li>
      ))}
    </ul>
  );
}

/** Spent vs a budget limit. Turns to the "over" color past 100%, with the words saying so too. */
export function Meter({ value, max, label }: { value: number; max: number; label: string }) {
  const ratio = max > 0 ? value / max : 0;
  const over = ratio > 1;
  return (
    <div
      className={over ? 'meter meter-over' : 'meter'}
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Math.min(value, max)}
    >
      <span className="meter-fill" style={{ ['--fill' as string]: `${Math.min(1, ratio) * 100}%` }} />
    </div>
  );
}

/* ---------------------------------------------------------------- donut */

export interface DonutDatum {
  key: string;
  label: string;
  value: number;
  color: string;
}

function arcPath(cx: number, cy: number, r0: number, r1: number, a0: number, a1: number): string {
  const p = (r: number, a: number) => `${cx + r * Math.sin(a)} ${cy - r * Math.cos(a)}`;
  const large = a1 - a0 > Math.PI ? 1 : 0;
  return [`M ${p(r1, a0)}`, `A ${r1} ${r1} 0 ${large} 1 ${p(r1, a1)}`, `L ${p(r0, a1)}`, `A ${r0} ${r0} 0 ${large} 0 ${p(r0, a0)}`, 'Z'].join(' ');
}

/** Pure layout: start/end angles (radians, clockwise from 12 o'clock) with a fixed pixel gap between slices. */
export function donutLayout(values: number[], radius: number, gapPx = 2): { a0: number; a1: number }[] {
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  if (total <= 0) return values.map(() => ({ a0: 0, a1: 0 }));
  const shown = values.filter((v) => v > 0).length;
  const gap = shown > 1 ? gapPx / radius : 0;
  const usable = Math.PI * 2 - gap * shown;
  let a = 0;
  return values.map((v) => {
    const sweep = (Math.max(0, v) / total) * usable;
    const slice = { a0: a + gap / 2, a1: a + gap / 2 + sweep };
    a += sweep + (v > 0 ? gap : 0);
    return slice;
  });
}

/**
 * Composition ring. Slices follow the given (series-slot) order clockwise
 * from 12 o'clock with a 2px surface gap; the center carries the figure.
 * `active` dims the rest (driven by the legend); clicks drill down.
 */
export function Donut({
  data,
  label,
  center,
  active,
  onActive,
  onSelect,
  size = 168,
  thickness = 24,
}: {
  data: DonutDatum[];
  label: string;
  center?: ReactNode;
  active?: string | null;
  onActive?: (key: string | null) => void;
  onSelect?: (key: string) => void;
  size?: number;
  thickness?: number;
}) {
  const r1 = size / 2;
  const r0 = r1 - thickness;
  const slices = donutLayout(
    data.map((d) => d.value),
    r1 - thickness / 2,
  );
  return (
    <div className="donut" style={{ ['--donut-default' as string]: `${size}px` }}>
      <svg viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label} onMouseLeave={() => onActive?.(null)}>
        {slices.every((s) => s.a1 <= s.a0) && <circle className="donut-track" cx={r1} cy={r1} r={r1 - thickness / 2} strokeWidth={thickness} fill="none" />}
        {data.map((d, i) => {
          const s = slices[i]!;
          if (s.a1 <= s.a0) return null;
          const dim = active && active !== d.key;
          return (
            <path
              key={d.key}
              d={arcPath(r1, r1, r0, r1, s.a0, Math.min(s.a1, s.a0 + Math.PI * 2 - 1e-4))}
              fill={d.color}
              className={dim ? 'donut-slice is-dim' : 'donut-slice'}
              onMouseEnter={() => onActive?.(d.key)}
              onClick={onSelect ? () => onSelect(d.key) : undefined}
            />
          );
        })}
      </svg>
      {center && <div className="donut-center">{center}</div>}
    </div>
  );
}

/**
 * Ranked horizontal bars, labels and values in text ink. One color for a
 * single series (subcategories of one category) or one per item
 * (categories side by side: each keeps its own line color).
 */
export function BarList({
  items,
  color = 'var(--ink-2)',
  format,
  label,
}: {
  items: { key: string; label: string; value: number; color?: string }[];
  color?: string;
  format: (v: number) => string;
  label: string;
}) {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="barlist" aria-label={label}>
      {items.map((i) => (
        <li key={i.key} className="barlist-item">
          <span className="barlist-label">{i.label}</span>
          <span className="barlist-value num">{format(i.value)}</span>
          <span className="barlist-track" aria-hidden>
            <span className="barlist-bar" style={{ background: i.color ?? color, ['--fill' as string]: `${(Math.max(0, i.value) / max) * 100}%` }} />
          </span>
        </li>
      ))}
    </ul>
  );
}

export interface BarDatum {
  key: string;
  label: string;
  value: number;
}

/**
 * Vertical bars over time (monthly totals). The highlighted bar is ink,
 * the rest recede; only the highlighted bar is direct-labeled, the rest
 * show their value on hover/focus.
 */
export function BarTrend({
  data,
  highlightKey,
  format,
  label,
  height = 168,
  reference,
  onSelect,
}: {
  data: BarDatum[];
  highlightKey?: string;
  format: (v: number) => string;
  label: string;
  height?: number;
  /** A dashed guide across the bars, e.g. the period average. */
  reference?: { value: number; label: string };
  /** Makes each bar a button (e.g. jump to that month). */
  onSelect?: (key: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const max = Math.max(1, ...data.map((d) => d.value), reference?.value ?? 0);
  const refPct = reference ? (reference.value / max) * 100 : null;
  return (
    <div
      className={onSelect ? 'bars bars-interactive' : 'bars'}
      role={onSelect ? 'group' : 'img'}
      aria-label={label}
      style={{ height, ['--ref' as string]: refPct === null ? undefined : `${refPct}%` }}
      onMouseLeave={() => setHover(null)}
    >
      {data.map((d, i) => {
        const active = d.key === highlightKey;
        const shown = active || d.key === hover;
        const Col = onSelect ? 'button' : 'div';
        return (
          <Col
            key={d.key}
            type={onSelect ? 'button' : undefined}
            className="bars-col"
            onMouseEnter={() => setHover(d.key)}
            onFocus={() => setHover(d.key)}
            onClick={onSelect ? () => onSelect(d.key) : undefined}
            aria-pressed={onSelect ? active : undefined}
            aria-label={onSelect ? `${d.label} ${format(d.value)}` : undefined}
          >
            <span className={shown ? 'bars-value num is-shown' : 'bars-value num'}>{format(d.value)}</span>
            <span className={refPct === null ? 'bars-track' : 'bars-track has-ref'}>
              <span className={active ? 'bars-bar is-active' : 'bars-bar'} style={{ ['--fill' as string]: `${(Math.max(0, d.value) / max) * 100}%` }} />
              {reference && i === 0 && <span className="bars-ref-label num">{reference.label}</span>}
            </span>
            <span className={active ? 'bars-label is-active' : 'bars-label'}>{d.label}</span>
          </Col>
        );
      })}
    </div>
  );
}

/**
 * A word-sized trend (a category row's last months). No axis: it shows the
 * shape, the row shows the amount. Missing months are skipped; the latest
 * point is marked. Scaled to its own min–max on purpose (shape, not size).
 */
export function Sparkline({ points, color = 'var(--ink-2)', label }: { points: (number | null)[]; color?: string; label: string }) {
  const known = points.map((p, i) => ({ p, i })).filter((x): x is { p: number; i: number } => x.p !== null);
  if (known.length < 2) return null;
  const min = Math.min(...known.map((k) => k.p));
  const max = Math.max(...known.map((k) => k.p));
  const span = max - min || 1;
  const X = (i: number) => 4 + (i / Math.max(1, points.length - 1)) * 92;
  const Y = (p: number) => 20 - ((p - min) / span) * 16;
  const last = known[known.length - 1]!;
  return (
    <svg className="sparkline" viewBox="0 0 100 24" preserveAspectRatio="none" role="img" aria-label={label}>
      <polyline points={known.map((k) => `${X(k.i)},${Y(k.p)}`).join(' ')} fill="none" stroke={color} strokeWidth={2} vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />
      {/* A zero-length round-capped stroke stays a true dot however the box is stretched. */}
      <line x1={X(last.i)} y1={Y(last.p)} x2={X(last.i)} y2={Y(last.p)} stroke={color} strokeWidth={6} strokeLinecap="round" vectorEffect="non-scaling-stroke" />
    </svg>
  );
}

/* ---------------------------------------------------------------- composition */

export interface CompositionSlice {
  key: string;
  label: string;
  value: number;
  color: string;
}

/**
 * Donut + its legend as one unit: shares as whole percentages that add up to
 * 100, hovering or focusing a legend row lights its slice and puts it in the
 * center, and choosing a slice or row calls onSelect (drill-down). Slices
 * are drawn in the order given: pass category (series-slot) order.
 */
export function CompositionChart({
  slices,
  label,
  format,
  formatShare,
  centerLabel,
  onSelect,
}: {
  slices: CompositionSlice[];
  label: string;
  format: (v: number) => string;
  formatShare: (percent: number) => string;
  centerLabel: string;
  onSelect?: (key: string) => void;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const shown = slices.filter((s) => s.value > 0);
  const total = shown.reduce((sum, s) => sum + s.value, 0);
  const shares = roundShares(shown.map((s) => s.value));
  const focus = hover ? shown.find((s) => s.key === hover) : undefined;
  return (
    <div className="composition">
      <Donut
        label={label}
        data={shown}
        active={hover}
        onActive={setHover}
        onSelect={onSelect}
        center={
          <>
            <span className="donut-figure num">{format(focus ? focus.value : total)}</span>
            <span className="donut-caption">{focus ? focus.label : centerLabel}</span>
          </>
        }
      />
      <ul className="composition-legend" aria-label={label}>
        {shown.map((s, i) => {
          const body = (
            <>
              <CategoryDot color={s.color} />
              <span className="legend-label">{s.label}</span>
              <span className="legend-value num">{formatShare(shares[i]!)}</span>
            </>
          );
          const dim = hover && hover !== s.key ? ' is-dim' : '';
          return (
            <li key={s.key}>
              {onSelect ? (
                <button
                  type="button"
                  className={`legend-button${dim}`}
                  onMouseEnter={() => setHover(s.key)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(s.key)}
                  onBlur={() => setHover(null)}
                  onClick={() => onSelect(s.key)}
                >
                  {body}
                </button>
              ) : (
                <span className={`legend-button legend-static${dim}`} onMouseEnter={() => setHover(s.key)} onMouseLeave={() => setHover(null)}>
                  {body}
                </span>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------- line */

export interface LineSeries {
  key: string;
  label: string;
  color: string;
  /** One value per x label; null = no data (a gap, e.g. before the budget existed). */
  values: (number | null)[];
  /** A second cue besides color when lines are compared (this month solid, last month dashed…). */
  dash?: 'solid' | 'dashed' | 'dotted';
  /** Stroke width; the line in focus can be heavier (default 2). */
  width?: number;
}

export function dashArray(dash: LineSeries['dash']): string | undefined {
  return dash === 'dashed' ? '6 4' : dash === 'dotted' ? '0.1 5' : undefined;
}

/** Round axis ticks (1 / 2 / 2.5 / 5 × 10ⁿ steps) covering [min, max], starting at or below 0. */
export function niceTicks(min: number, max: number, count = 4): number[] {
  const lo = Math.min(0, min);
  const hi = Math.max(lo + 1, max);
  const raw = (hi - lo) / Math.max(1, count);
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = ([1, 2, 2.5, 5, 10].find((m) => m * mag >= raw) ?? 10) * mag;
  const ticks: number[] = [];
  for (let v = Math.floor(lo / step) * step; v < hi + step - 1e-9; v += step) {
    ticks.push(Math.round(v * 1e6) / 1e6);
    if (v >= hi) break;
  }
  return ticks;
}

/** Which x labels to print so they never collide: every k-th, always counting back from the last one. */
export function thinLabels(count: number, plotWidth: number, minGap = 44): boolean[] {
  const fit = Math.max(1, Math.floor(plotWidth / minGap));
  const every = Math.max(1, Math.ceil(count / fit));
  return Array.from({ length: count }, (_, i) => (count - 1 - i) % every === 0);
}

/** Runs of consecutive non-null points: each becomes one path (gaps stay gaps). */
export function lineRuns(values: (number | null)[]): { start: number; values: number[] }[] {
  const runs: { start: number; values: number[] }[] = [];
  values.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) return;
    const last = runs[runs.length - 1];
    if (last && last.start + last.values.length === i) last.values.push(v);
    else runs.push({ start: i, values: [v] });
  });
  return runs;
}

function useWidth<T extends HTMLElement>(fallback: number) {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(fallback);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const update = () => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

/**
 * Values over time on one y-axis. 2px lines, recessive grid, a crosshair
 * with an 8px marker per series and a tooltip under the pointer (touch,
 * mouse, or ←/→ when focused). Gaps stay gaps. Series are drawn in the order
 * given; pass at most a few, and only colors that stay apart as crossing
 * lines, otherwise facet into several single-series charts (compact).
 *
 * `compact` is for small multiples: no x labels, and the y-axis reduced to
 * its top value printed over the chart (the base is always 0), so each
 * multiple can keep its own scale and still say what it is.
 */
export function LineChart({
  labels,
  series,
  format,
  formatTick = format,
  label,
  height = 180,
  compact = false,
  yMax,
  reference,
  highlightIndex,
}: {
  labels: { key: string; label: string }[];
  series: LineSeries[];
  format: (v: number) => string;
  /** Shorter figures for the y-axis. */
  formatTick?: (v: number) => string;
  label: string;
  height?: number;
  compact?: boolean;
  /** Fix the top of the scale (shared by small multiples). */
  yMax?: number;
  /** A dashed guide, e.g. the average. */
  reference?: { value: number; label?: string };
  /** Keep a marker on one x (e.g. the selected month). */
  highlightIndex?: number;
}) {
  const [wrap, width] = useWidth<HTMLDivElement>(320);
  const [hover, setHover] = useState<number | null>(null);
  const n = labels.length;
  const all = series.flatMap((s) => s.values.filter((v): v is number => v !== null));
  const dataMax = Math.max(0, ...all, reference?.value ?? 0, yMax ?? 0);
  const dataMin = Math.min(0, ...all);
  const ticks = niceTicks(dataMin, dataMax, compact ? 2 : 4);
  const top = ticks[ticks.length - 1]!;
  const bottom = ticks[0]!;
  const tickWidth = compact ? 0 : Math.max(...ticks.map((t) => formatTick(t).length)) * 7 + 8;
  const pad = { l: tickWidth, r: 10, t: compact ? 18 : 10, b: compact ? 6 : 24 };
  const plotW = Math.max(40, width - pad.l - pad.r);
  const plotH = Math.max(24, height - pad.t - pad.b);
  const x = (i: number) => pad.l + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v: number) => pad.t + plotH * (1 - (v - bottom) / (top - bottom || 1));
  const printed = thinLabels(n, plotW);
  const active = hover ?? null;

  const pick = (clientX: number, target: Element) => {
    const box = target.getBoundingClientRect();
    const px = clientX - box.left;
    let best = 0;
    for (let i = 1; i < n; i++) if (Math.abs(x(i) - px) < Math.abs(x(best) - px)) best = i;
    setHover(best);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const from = hover ?? highlightIndex ?? n - 1;
      setHover(Math.max(0, Math.min(n - 1, from + (e.key === 'ArrowRight' ? 1 : -1))));
    } else if (e.key === 'Escape') setHover(null);
  };

  const tipX = active === null ? 0 : x(active);
  const tipRows =
    active === null
      ? []
      : series
          .map((s) => ({ s, v: s.values[active] ?? null }))
          .filter((r) => r.v !== null)
          .sort((a, b) => (b.v as number) - (a.v as number));

  return (
    <div
      ref={wrap}
      className={compact ? 'line line-compact' : 'line'}
      role="group"
      aria-label={label}
      tabIndex={0}
      onKeyDown={onKey}
      onBlur={() => setHover(null)}
    >
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label={label}
        onPointerMove={(e: ReactPointerEvent<SVGSVGElement>) => pick(e.clientX, e.currentTarget)}
        onPointerDown={(e: ReactPointerEvent<SVGSVGElement>) => pick(e.clientX, e.currentTarget)}
        onPointerLeave={() => setHover(null)}
      >
        {ticks.map((t) => (
          <g key={t}>
            <line className={t === 0 ? 'line-grid line-base' : 'line-grid'} x1={pad.l} x2={pad.l + plotW} y1={y(t)} y2={y(t)} />
            {!compact && (
              <text className="line-tick num" x={pad.l - 8} y={y(t)} dy="0.32em" textAnchor="end">
                {formatTick(t)}
              </text>
            )}
          </g>
        ))}
        {compact && (
          <text className="line-tick num" x={pad.l} y={y(top) - 5}>
            {formatTick(top)}
          </text>
        )}
        {reference && <line className="line-ref" x1={pad.l} x2={pad.l + plotW} y1={y(reference.value)} y2={y(reference.value)} />}
        {!compact &&
          labels.map((l, i) =>
            printed[i] ? (
              <text key={l.key} className={i === highlightIndex ? 'line-xlabel is-active' : 'line-xlabel'} x={x(i)} y={height - 6} textAnchor="middle">
                {l.label}
              </text>
            ) : null,
          )}
        {active !== null && <line className="line-cross" x1={tipX} x2={tipX} y1={pad.t} y2={pad.t + plotH} />}
        {series.map((s) =>
          lineRuns(s.values).map((run) =>
            run.values.length === 1 ? (
              <circle key={`${s.key}-${run.start}`} cx={x(run.start)} cy={y(run.values[0]!)} r={3} fill={s.color} />
            ) : (
              <path
                key={`${s.key}-${run.start}`}
                className="line-path"
                d={run.values.map((v, j) => `${j ? 'L' : 'M'}${x(run.start + j).toFixed(1)} ${y(v).toFixed(1)}`).join(' ')}
                stroke={s.color}
                strokeWidth={s.width ?? 2}
                strokeDasharray={dashArray(s.dash)}
              />
            ),
          ),
        )}
        {series.map((s) => {
          const at = active ?? highlightIndex;
          const v = at === undefined || at === null ? null : (s.values[at] ?? null);
          return v === null ? null : <circle key={`m-${s.key}`} className="line-marker" cx={x(at!)} cy={y(v)} r={4} fill={s.color} />;
        })}
      </svg>
      {reference?.label && !compact && (
        <span className="line-ref-label num" style={{ top: y(reference.value) - 18, right: pad.r }}>
          {reference.label}
        </span>
      )}
      {active !== null && tipRows.length > 0 && (
        <div
          className="line-tip"
          role="presentation"
          // Beside the crosshair, never over the point it describes: right of it in the left half, left of it in the right half.
          style={tipX < width / 2 ? { left: tipX + 12 } : { right: width - tipX + 12 }}
        >
          <span className="line-tip-title">{labels[active]!.label}</span>
          {tipRows.map(({ s, v }) => (
            <span key={s.key} className="line-tip-row">
              {series.length > 1 && <CategoryDot color={s.color} size={8} />}
              {series.length > 1 && <span className="line-tip-label">{s.label}</span>}
              <span className="num">{format(v as number)}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- compare */

export interface CompareDatum {
  key: string;
  label: string;
  color: string;
  current: number;
  base: number;
}

/**
 * Two periods side by side, item by item: the current period as a solid
 * bar in the item's color, the period it is compared with as a pale bar of
 * the same color underneath, on one shared scale. The change is written out
 * (sign and amount), so it never depends on reading bar lengths or color.
 */
export function CompareBars({
  items,
  format,
  formatDelta,
  label,
  currentLabel,
  baseLabel,
  unchangedLabel,
}: {
  items: CompareDatum[];
  format: (v: number) => string;
  /** Signed change text, e.g. "+$40". */
  formatDelta: (v: number) => string;
  label: string;
  currentLabel: string;
  baseLabel: string;
  /** Shown instead of a zero change. */
  unchangedLabel: string;
}) {
  const max = Math.max(1, ...items.flatMap((i) => [i.current, i.base]));
  const pct = (v: number) => `${(Math.max(0, v) / max) * 100}%`;
  return (
    <div className="compare">
      <ul className="compare-key" aria-hidden>
        <li>
          <span className="compare-swatch" />
          {currentLabel}
        </li>
        <li>
          <span className="compare-swatch is-base" />
          {baseLabel}
        </li>
      </ul>
      <ul className="compare-list" aria-label={label}>
        {items.map((i) => {
          const delta = i.current - i.base;
          return (
            <li key={i.key} className="compare-item">
              <span className="compare-head">
                <CategoryDot color={i.color} />
                <span className="compare-label">{i.label}</span>
                <span className={delta > 0 ? 'compare-delta num is-up' : delta < 0 ? 'compare-delta num is-down' : 'compare-delta num'}>{delta > 0 ? '↑ ' : delta < 0 ? '↓ ' : ''}
                  {delta === 0 ? unchangedLabel : formatDelta(delta)}
                </span>
              </span>
              <span className="compare-row">
                <span className="compare-track" aria-hidden>
                  <span className="compare-bar" style={{ background: i.color, width: pct(i.current) }} />
                </span>
                <span className="compare-value num">
                  <span className="visually-hidden">{currentLabel}: </span>
                  {format(i.current)}
                </span>
              </span>
              <span className="compare-row is-base">
                <span className="compare-track" aria-hidden>
                  <span className="compare-bar is-base" style={{ ['--bar' as string]: i.color, width: pct(i.base) }} />
                </span>
                <span className="compare-value num">
                  <span className="visually-hidden">{baseLabel}: </span>
                  {format(i.base)}
                </span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---------------------------------------------------------------- treemap */

export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Squarified treemap layout (Bruls, Huizing, van Wijk): tiles as close to
 * square as the values allow. Returns one rect per value, in input order;
 * zero or negative values get an empty rect.
 */
export function squarify(values: readonly number[], area: Rect): Rect[] {
  const out: Rect[] = values.map(() => ({ x: area.x, y: area.y, w: 0, h: 0 }));
  const total = values.reduce((s, v) => s + Math.max(0, v), 0);
  if (total <= 0 || area.w <= 0 || area.h <= 0) return out;
  const scale = (area.w * area.h) / total;
  const queue = values
    .map((v, i) => ({ i, a: Math.max(0, v) * scale }))
    .filter((q) => q.a > 0)
    .sort((p, q) => q.a - p.a);
  const free = { ...area };
  const worst = (row: { a: number }[], side: number) => {
    const s = row.reduce((t, r) => t + r.a, 0);
    const max = Math.max(...row.map((r) => r.a));
    const min = Math.min(...row.map((r) => r.a));
    return Math.max((side * side * max) / (s * s), (s * s) / (side * side * min));
  };
  const place = (row: { i: number; a: number }[]) => {
    const s = row.reduce((t, r) => t + r.a, 0);
    if (free.w >= free.h) {
      const w = s / free.h;
      let y = free.y;
      for (const r of row) {
        const h = r.a / w;
        out[r.i] = { x: free.x, y, w, h };
        y += h;
      }
      free.x += w;
      free.w -= w;
    } else {
      const h = s / free.w;
      let x = free.x;
      for (const r of row) {
        const w = r.a / h;
        out[r.i] = { x, y: free.y, w, h };
        x += w;
      }
      free.y += h;
      free.h -= h;
    }
  };
  let row: { i: number; a: number }[] = [];
  for (const item of queue) {
    const side = Math.min(free.w, free.h);
    if (row.length === 0 || worst([...row, item], side) <= worst(row, side)) row.push(item);
    else {
      place(row);
      row = [item];
    }
  }
  if (row.length) place(row);
  return out;
}

export interface TreemapGroup {
  key: string;
  label: string;
  color: string;
  children: { key: string; label: string; value: number }[];
}

/**
 * Where the money went, all at once: one block per category (its line
 * color), split into its subcategories. Tiles are tinted, not filled, so
 * the ink labels stay readable on every series color; the category's full
 * color runs along each tile's top edge. Labels show where they fit; every
 * tile names itself to assistive tech and on hover.
 */
export function Treemap({
  groups,
  label,
  format,
  height = 260,
  onSelect,
}: {
  groups: TreemapGroup[];
  label: string;
  format: (v: number) => string;
  height?: number;
  /** A tile was chosen: its group and child keys. */
  onSelect?: (groupKey: string, childKey: string) => void;
}) {
  const [wrap, width] = useWidth<HTMLDivElement>(320);
  const [hover, setHover] = useState<string | null>(null);
  const shown = groups.map((g) => ({ ...g, children: g.children.filter((c) => c.value > 0) })).filter((g) => g.children.length > 0);
  const total = shown.reduce((s, g) => s + g.children.reduce((t, c) => t + c.value, 0), 0);
  const outer = squarify(
    shown.map((g) => g.children.reduce((t, c) => t + c.value, 0)),
    { x: 0, y: 0, w: width, h: height },
  );
  const GAP = 2;
  const tiles = shown.flatMap((g, gi) => {
    const r = outer[gi]!;
    const inner = squarify(
      g.children.map((c) => c.value),
      { x: r.x + GAP, y: r.y + GAP, w: Math.max(0, r.w - GAP * 2), h: Math.max(0, r.h - GAP * 2) },
    );
    return g.children.map((c, ci) => ({ group: g, child: c, rect: inner[ci]! }));
  });
  const tip = hover ? tiles.find((x) => `${x.group.key}/${x.child.key}` === hover) : undefined;

  return (
    <div ref={wrap} className="treemap" style={{ height }} role="group" aria-label={label} onMouseLeave={() => setHover(null)}>
      {tiles.map(({ group, child, rect }) => {
        const key = `${group.key}/${child.key}`;
        const w = Math.max(0, rect.w - 1);
        const h = Math.max(0, rect.h - 1);
        const roomy = w >= 76 && h >= 42;
        const named = roomy || (w >= 56 && h >= 22);
        const share = total > 0 ? Math.round((child.value / total) * 100) : 0;
        const name = child.label === group.label ? child.label : `${child.label}, ${group.label}`;
        const Tag = onSelect ? 'button' : 'div';
        return (
          <Tag
            key={key}
            type={onSelect ? 'button' : undefined}
            className={hover && hover !== key ? 'treemap-tile is-dim' : 'treemap-tile'}
            style={{ left: rect.x, top: rect.y, width: w, height: h, ['--tile' as string]: group.color }}
            aria-label={`${name}: ${format(child.value)}, ${share}%`}
            onMouseEnter={() => setHover(key)}
            onFocus={() => setHover(key)}
            onBlur={() => setHover(null)}
            onClick={onSelect ? () => onSelect(group.key, child.key) : undefined}
          >
            {named && (
              <span className="treemap-text" aria-hidden>
                <span className="treemap-label">{child.label}</span>
                {roomy && <span className="treemap-value num">{format(child.value)}</span>}
              </span>
            )}
          </Tag>
        );
      })}
      {tip && (
        <span
          className="chart-tip treemap-tip"
          role="presentation"
          style={{
            left: Math.min(Math.max(0, tip.rect.x), Math.max(0, width - 180)),
            top: Math.min(tip.rect.y + tip.rect.h + 4, height + 4),
          }}
        >
          <CategoryDot color={tip.group.color} size={8} />
          {tip.child.label === tip.group.label ? tip.child.label : `${tip.group.label} · ${tip.child.label}`} · {format(tip.child.value)}
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- waterfall */

export interface WaterfallStep {
  key: string;
  label: string;
  /** A total (from 0) or a change (from the running total). */
  kind: 'total' | 'change';
  value: number;
  color?: string;
}

/**
 * Pure layout: where each bar starts and ends, and the scale. When every
 * running total stays well above zero the scale starts at a round value
 * below the lowest one, so the changes are visible; `truncated` says so and
 * the total bars show a break.
 */
export function waterfallLayout(steps: readonly Pick<WaterfallStep, 'kind' | 'value'>[]) {
  let running = 0;
  const bars = steps.map((s) => {
    if (s.kind === 'total') {
      running = s.value;
      return { from: 0, to: s.value };
    }
    const from = running;
    running += s.value;
    return { from, to: running };
  });
  const ends = bars.flatMap((b) => [b.from, b.to]);
  const hi = Math.max(1, ...ends);
  const levels = bars.map((b, i) => (steps[i]!.kind === 'total' ? b.to : Math.min(b.from, b.to)));
  const low = Math.min(...levels);
  const step = niceTicks(0, hi, 5)[1] ?? hi;
  const lo = low > hi * 0.5 ? Math.max(0, Math.floor((low - step / 2) / step) * step) : 0;
  return { bars, lo, hi, truncated: lo > 0 };
}

/**
 * From one total to another, change by change (the "bridge"): what made
 * this month different from the one it is compared with. Rows read top to
 * bottom; each change floats from the running total, increases in the
 * "up" color with ↑, decreases in "down" with ↓, amounts written out.
 */
export function Waterfall({
  steps,
  label,
  format,
  formatChange,
  scaleNote,
}: {
  steps: WaterfallStep[];
  label: string;
  format: (v: number) => string;
  formatChange: (v: number) => string;
  /** Shown when the scale does not start at 0, e.g. "Scale starts at $1,000". */
  scaleNote: (lo: number) => string;
}) {
  const { bars, lo, hi, truncated } = waterfallLayout(steps);
  const pos = (v: number) => ((Math.max(lo, v) - lo) / (hi - lo || 1)) * 100;
  return (
    <div className="waterfall">
      <ul className="waterfall-list" aria-label={label}>
        {steps.map((s, i) => {
          const b = bars[i]!;
          const left = pos(Math.min(b.from, b.to));
          const width = Math.max(0.6, pos(Math.max(b.from, b.to)) - left);
          const dir = s.kind === 'total' ? 'total' : s.value > 0 ? 'up' : s.value < 0 ? 'down' : 'flat';
          return (
            <li key={s.key} className={`waterfall-row is-${dir}`}>
              <span className="waterfall-label">
                {s.color && s.kind === 'change' && <CategoryDot color={s.color} size={8} />}
                {s.label}
              </span>
              <span className="waterfall-track" aria-hidden>
                {s.kind === 'change' && <span className="waterfall-link" style={{ left: `${pos(b.from)}%` }} />}
                <span className={truncated && s.kind === 'total' ? 'waterfall-bar is-broken' : 'waterfall-bar'} style={{ left: `${left}%`, width: `${width}%` }} />
              </span>
              <span className="waterfall-value num">
                {dir === 'up' ? '↑ ' : dir === 'down' ? '↓ ' : ''}
                {s.kind === 'total' ? format(s.value) : formatChange(s.value)}
              </span>
            </li>
          );
        })}
      </ul>
      {truncated && <p className="waterfall-note">{scaleNote(lo)}</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- line key */

/** Legend for lines told apart by dash as well as color. */
export function LineKey({ items }: { items: { key: string; label: string; color: string; dash?: LineSeries['dash'] }[] }) {
  return (
    <ul className="line-key">
      {items.map((i) => (
        <li key={i.key}>
          <svg width="24" height="8" aria-hidden>
            <line x1="1" x2="23" y1="4" y2="4" stroke={i.color} strokeWidth={2} strokeLinecap="round" strokeDasharray={dashArray(i.dash)} />
          </svg>
          {i.label}
        </li>
      ))}
    </ul>
  );
}

/* ---------------------------------------------------------------- heat grid */

export interface HeatCell {
  key: string;
  /** Shown in the square (a day number, a weekday). */
  text: string;
  /** What a screen reader says: the day and its amount. */
  label: string;
  /** 0 = nothing … `levels`. */
  level: number;
  /** Before the data starts or still to come: drawn as an outline, not a value. */
  outside?: boolean;
  /** Marked with a ring (today). */
  current?: boolean;
}

/**
 * Magnitude on a grid (a month's days, the seven weekdays): one ink ramp,
 * lighter = less, never a category color. Quantized to at most four steps
 * so a square reads at a glance; the exact amounts are in each label and
 * in the container's table. `null` pads the grid (days before the 1st).
 */
export function HeatGrid({
  cells,
  columns,
  levels,
  label,
  onSelect,
  footer,
}: {
  cells: (HeatCell | null)[];
  /** Column headings (weekday initials; all empty = no heading row); hidden from screen readers, the labels say it. */
  columns: string[];
  levels: number;
  label: string;
  onSelect?: (key: string) => void;
  /** One small line under each column (e.g. its amount). */
  footer?: string[];
}) {
  // The ramp always spans its four shades, however many steps the data has.
  const shade = (level: number) => (level <= 0 || levels <= 0 ? 0 : Math.max(1, Math.round((level * 4) / levels)));
  return (
    <div className="heat" role="group" aria-label={label} style={{ ['--heat-cols' as string]: columns.length }}>
      {columns.some(Boolean) && (
        <div className="heat-row heat-head" aria-hidden>
          {columns.map((c, i) => (
            <span key={i}>{c}</span>
          ))}
        </div>
      )}
      <div className="heat-row">
        {cells.map((c, i) => {
          if (!c) return <span key={`pad${i}`} className="heat-pad" aria-hidden />;
          const className = ['heat-cell', `heat-${c.outside ? 'outside' : shade(c.level)}`, c.current && 'heat-current'].filter(Boolean).join(' ');
          return onSelect && !c.outside ? (
            <button key={c.key} type="button" className={className} aria-label={c.label} onClick={() => onSelect(c.key)}>
              {c.text}
            </button>
          ) : (
            <span key={c.key} className={className} role="img" aria-label={c.label}>
              {c.text}
            </span>
          );
        })}
      </div>
      {footer && (
        <div className="heat-row heat-foot num" aria-hidden>
          {footer.map((f, i) => (
            <span key={i}>{f}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/** "Less ▢▢▢▢▢ More": the ramp's shades in order. */
export function HeatKey({ less, more }: { less: string; more: string }) {
  return (
    <p className="heat-key">
      <span>{less}</span>
      {[0, 1, 2, 3, 4].map((s) => (
        <span key={s} className={`heat-swatch heat-${s}`} aria-hidden />
      ))}
      <span>{more}</span>
    </p>
  );
}
